export type TaskEventSource = 'app-server' | 'workflow'
export type TaskEventKind = 'lifecycle' | 'plan' | 'diff' | 'item' | 'delta' | 'reasoning' | 'command' | 'approval' | 'tool' | 'search' | 'image' | 'review' | 'compaction' | 'reroute' | 'usage' | 'warning' | 'error' | 'workflow' | 'agent' | 'browser' | 'team' | 'diagnostic'

export type TaskEvent = {
  schemaVersion: number
  eventId: string
  sequence: number
  timestamp: number
  taskId: string
  source: TaskEventSource
  kind: TaskEventKind
  payload: Record<string, unknown>
  method?: string
  threadId?: string
  turnId?: string
  itemId?: string
  workflowId?: string
  projectId?: string
  cwd?: string
}

export type TaskItem = { id: string; type: string; status?: string; event: TaskEvent }
export type TaskMessage = { id: string; role: 'user' | 'assistant' | 'notice'; content: string; streaming?: boolean; phase?: 'commentary' | 'final_answer' }
export type TaskStatus = 'idle' | 'queued' | 'running' | 'completed' | 'interrupted' | 'failed' | 'cancelled'
export type PlanStepState = 'pending' | 'running' | 'completed' | 'failed' | 'retrying' | 'reviewing' | 'paused'
export type PlanState = {
  id: string
  title: string
  status: string
  source: 'codex' | 'workflow'
  owner?: { agent?: string; provider?: string; model?: string }
  explanation?: string
  activeStepId?: string | null
  steps: Array<{ id: string; title: string; description?: string; status: PlanStepState | string; attempts?: number; retries?: number; checks?: string; review?: string; owner?: { agent?: string; provider?: string; model?: string }; files?: string[]; commands?: string[] }>
  checks?: string
  review?: { decision?: string; summary?: string }
  agents?: Array<{ id: string; role?: string; title?: string; model?: string; state?: string; statusSummary?: string; assignedStepId?: string | null; profileId?: string | null; avatar?: Record<string, unknown> | null }>
  agentMessages?: Array<{ id: string; at?: string; from: string; agentId?: string | null; preview: string }>
  team?: TeamRoomState
  updatedAt?: string | number
}

export type TeamAgent = {
  id: string
  profileId?: string | null
  displayName?: string
  role?: string
  roleLabel?: string
  title?: string
  model?: string
  effort?: string
  state?: string
  statusSummary?: string
  assignedStepId?: string | null
  avatar?: Record<string, unknown> | null
  workspaceLease?: { scope?: string; mode?: string; status?: string } | null
  [key: string]: unknown
}

export type TeamMessage = {
  id: string
  taskId?: string | null
  sequence: number
  from: { type: 'user' | 'agent' | 'system'; id: string }
  fromLabel?: string
  to?: { mode: 'lead' | 'agent' | 'agents' | 'room'; ids: string[] }
  kind: string
  text: string
  preview?: string
  replyTo?: string | null
  decisionRefs?: string[]
  contextRefs?: string[]
  createdAt?: string | null
  at?: string | null
  agentId?: string | null
}

export type TeamDecision = { id: string; status?: string; statement?: string; scope?: string[]; authority?: string; [key: string]: unknown }
export type TeamConflict = { id: string; status?: string; summary?: string; [key: string]: unknown }
export type TeamRoomState = {
  workflowId: string
  graphId?: string
  title?: string
  state?: string
  version?: number
  roomSequence: number
  agents: TeamAgent[]
  messages: TeamMessage[]
  decisions: TeamDecision[]
  conflicts: TeamConflict[]
  updatedAt?: string | number
}

export type TaskState = {
  taskId: string
  events: TaskEvent[]
  seen: ReadonlySet<string>
  items: TaskItem[]
  messages: TaskMessage[]
  plan?: PlanState
  status: TaskStatus
  running: boolean
  activeTurnId?: string
  diagnostics: string[]
  unread: number
  scrollTop: number
  draft: string
}

export const emptyTaskState = (taskId: string): TaskState => ({
  taskId, events: [], seen: new Set<string>(), items: [], messages: [], status: 'idle', running: false,
  diagnostics: [], unread: 0, scrollTop: 0, draft: '', plan: undefined,
})
